import { apiClient } from './apiClient';
import { db } from '../lib/database';
import { API_ENDPOINTS } from '../config/api';
import { questionResponseService } from './questionResponseService';

const normalizeForm = form => {
  if (!form) return null;

  const allQuestions = (form.sections || []).flatMap(s => s.fields || []);

  return {
    ...form,
    id: String(form.id),
    name: form.name?.trim() || form.title?.trim(),
    title: form.name?.trim() || form.title?.trim(),
    questions: allQuestions
  };
};

const mapQuestionsToSections = questions => {
  if (!questions || questions.length === 0) return [];

  const sectionMap = new Map();

  const defaultSection = {
    id: 'default-section',
    name: 'Form Fields',
    order: 9999,
    fields: []
  };

  questions.forEach(question => {
    const sectionId = question.section_id || defaultSection.id;
    if (!sectionMap.has(sectionId)) {
      sectionMap.set(sectionId, {
        id: sectionId,
        name: question.section_name || defaultSection.name,
        order: question.section_order !== undefined ? question.section_order : defaultSection.order,
        fields: []
      });
    }
    sectionMap.get(sectionId).fields.push(question);
  });

  const sections = Array.from(sectionMap.values());
  sections.sort((a, b) => (a.order || 0) - (b.order || 0));
  sections.forEach(section => {
    section.fields.sort((a, b) => (a.order || 0) - (b.order || 0));
  });
  return sections;
};

export const formsService = {
  getAllLocal: () => {
    return db.forms.toArray();
  },

  syncAll: () => {
    if (!navigator.onLine) return Promise.resolve([]);

    return apiClient.get(API_ENDPOINTS.forms.list).then(listResponse => {
      const responseData = listResponse.data.data;
      const apiFormsList = responseData?.data || responseData || [];

      return db.forms.toArray().then(localForms => {
        const apiFormIds = new Set(apiFormsList.map(f => String(f.id)));
        const localFormMap = new Map(localForms.map(f => [String(f.id), f]));

        const formsToDelete = localForms.filter(localForm => !apiFormIds.has(String(localForm.id))).map(localForm => String(localForm.id));

        const formsToFetchDetails = [];

        apiFormsList.forEach(apiForm => {
          const formId = String(apiForm.id);
          const localForm = localFormMap.get(formId);
          if (!localForm || apiForm.updated_at !== localForm.updated_at) {
            formsToFetchDetails.push(formId);
          }
        });

        return db
          .transaction('rw', db.forms, db.form_questions, () => {
            if (formsToDelete.length > 0) {
              return db.forms.bulkDelete(formsToDelete).then(() => db.form_questions.where('form_id').anyOf(formsToDelete).delete());
            }
            return Promise.resolve();
          })
          .then(() => {
            if (formsToFetchDetails.length > 0) {
              const detailPromises = formsToFetchDetails.map(id => formsService.syncDetails(id).catch(() => null));
              return Promise.all(detailPromises);
            }
            return Promise.resolve();
          })
          .then(() => {
            return db.transaction('rw', db.forms, () => {
              const updatePromises = apiFormsList.map(apiForm => {
                const updates = {
                  submissions_exists: apiForm.submissions_exists
                };
                if (apiForm.project) {
                  updates.project = apiForm.project;
                }
                if (apiForm.project_id) {
                  updates.project_id = apiForm.project_id;
                }
                return db.forms
                  .update(String(apiForm.id), updates)
                  .catch(() => {});
              });
              return Promise.all(updatePromises);
            });
          })
          .then(() => formsService.getAllLocal());
      });
    });
  },

  getLocalById: formId => {
    const formIdStr = String(formId);
    return db.forms.get(formIdStr).then(localForm => {
      if (!localForm) return null;

      return db.form_questions
        .where('form_id')
        .equals(formIdStr)
        .toArray()
        .then(questions => {
          const parsedQuestions = questions.map(q => ({
            ...q,
            options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : []
          }));

          localForm.questions = parsedQuestions;
          if (!localForm.sections || localForm.sections.length === 0) {
            localForm.sections = mapQuestionsToSections(parsedQuestions);
          }
          return localForm;
        });
    });
  },

  syncDetails: formId => {
    if (!navigator.onLine) return Promise.resolve(null);
    const formIdStr = String(formId);

    return apiClient.get(API_ENDPOINTS.forms.details(formIdStr)).then(response => {
      const responseData = response.data.data;
      const form = normalizeForm(responseData?.data || responseData);
      if (form) {
        return db
          .transaction('rw', db.forms, db.form_questions, () => {
            return db.forms.put(form).then(() => questionResponseService.storeFormQuestions(form));
          })
          .then(() => form);
      }
      return null;
    });
  },

  getById: (formId, forceOffline = false) => {
    const formIdStr = String(formId);

    return formsService.getLocalById(formIdStr).then(localForm => {
      if (forceOffline || !navigator.onLine) {
        if (!localForm) {
          return Promise.reject(new Error(`Form ${formIdStr} not found locally.`));
        }
        return { data: { data: localForm } };
      }
      return formsService
        .syncDetails(formIdStr)
        .then(apiForm => ({ data: { data: apiForm || localForm } }))
        .catch(error => {
          if (localForm) return { data: { data: localForm } };
          throw error;
        });
    });
  },
  fetchAll: () => {
    return formsService.syncAll().then(forms => ({ data: { data: forms } }));
  }
};
